'use client'

import { useState, useRef, KeyboardEvent } from 'react'
import { hasChildren, getChildren } from './helpers'
import {
  NetworkTreeNode,
  UseTreeNavigationProps,
  UseTreeNavigationResult
} from './types'

export function useTreeNavigation({
  rootNodes,
  filteredItems,
  expandedNodes,
  toggleNode,
  onSelect,
  isComposing,
  setSearchTerm,
  resultsRef,
  disabledNodeIds
}: UseTreeNavigationProps): UseTreeNavigationResult {
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(
    rootNodes[0]?.network.id ?? null
  )
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const scrollFocusedNodeIntoView = (nodeId: string) => {
    if (resultsRef?.current) {
      const focusedElement = resultsRef.current.querySelector(
        `[data-node-id="${nodeId}"]`
      )
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }

  const getVisibleNodes = () => {
    const visible: NetworkTreeNode[] = []

    const addVisibleNodes = (nodes: NetworkTreeNode[]) => {
      nodes.forEach(node => {
        visible.push(node)
        if (expandedNodes.has(node.network.id)) {
          addVisibleNodes(getChildren(node, filteredItems))
        }
      })
    }

    addVisibleNodes(rootNodes)
    return visible
  }

  const getEnabledVisibleNodes = () => {
    return getVisibleNodes().filter(
      node => !disabledNodeIds.has(node.network.id)
    )
  }

  const handleSelect = (node: NetworkTreeNode) => {
    if (disabledNodeIds.has(node.network.id)) {
      return
    }

    setSelectedNodeId(node.network.id)
    onSelect(node)
  }

  const moveHighlight = (delta: number) => {
    const enabledVisibleNodes = getEnabledVisibleNodes()
    if (enabledVisibleNodes.length === 0) return

    const currentIndex = enabledVisibleNodes.findIndex(
      node => node.network.id === focusedNodeId
    )

    let nextIndex: number
    if (currentIndex === -1) {
      nextIndex = 0
    } else {
      nextIndex = Math.max(
        0,
        Math.min(currentIndex + delta, enabledVisibleNodes.length - 1)
      )
    }

    if (nextIndex !== currentIndex) {
      const nextNodeId = enabledVisibleNodes[nextIndex].network.id
      setFocusedNodeId(nextNodeId)
      scrollFocusedNodeIntoView(nextNodeId)
    }
  }

  const moveToFirstChild = (
    currentNode: NetworkTreeNode,
    currentIndex: number,
    visibleNodes: NetworkTreeNode[]
  ) => {
    const nextNode = visibleNodes[currentIndex + 1]
    if (
      nextNode &&
      nextNode.viewableParent?.id === currentNode.network.id &&
      !disabledNodeIds.has(nextNode.network.id)
    ) {
      setFocusedNodeId(nextNode.network.id)
      scrollFocusedNodeIntoView(nextNode.network.id)
    }
  }

  const moveToParent = (
    currentNode: NetworkTreeNode,
    visibleNodes: NetworkTreeNode[]
  ) => {
    if (currentNode.viewableParent) {
      const parentNode = visibleNodes.find(
        node => node.network.id === currentNode.viewableParent?.id
      )
      if (parentNode && !disabledNodeIds.has(parentNode.network.id)) {
        setFocusedNodeId(parentNode.network.id)
        scrollFocusedNodeIntoView(parentNode.network.id)
      }
    }
  }

  const keyboardActions = {
    ArrowUp: (
      e: KeyboardEvent<HTMLInputElement>,
      _currentNode?: NetworkTreeNode,
      _currentIndex?: number,
      _visibleNodes?: NetworkTreeNode[]
    ) => {
      e.preventDefault()
      moveHighlight(-1)
    },
    ArrowDown: (
      e: KeyboardEvent<HTMLInputElement>,
      _currentNode?: NetworkTreeNode,
      _currentIndex?: number,
      _visibleNodes?: NetworkTreeNode[]
    ) => {
      e.preventDefault()
      moveHighlight(1)
    },
    ArrowRight: (
      e: KeyboardEvent<HTMLInputElement>,
      currentNode: NetworkTreeNode,
      currentIndex: number,
      visibleNodes: NetworkTreeNode[]
    ) => {
      e.preventDefault()
      if (disabledNodeIds.has(currentNode.network.id)) {
        return
      }

      if (currentNode && hasChildren(currentNode, filteredItems)) {
        if (!expandedNodes.has(currentNode.network.id)) {
          toggleNode(currentNode.network.id)
        } else {
          moveToFirstChild(currentNode, currentIndex, visibleNodes)
        }
      }
    },
    ArrowLeft: (
      e: KeyboardEvent<HTMLInputElement>,
      currentNode: NetworkTreeNode,
      currentIndex: number,
      visibleNodes: NetworkTreeNode[]
    ) => {
      e.preventDefault()
      if (disabledNodeIds.has(currentNode.network.id)) {
        return
      }

      if (currentNode) {
        if (expandedNodes.has(currentNode.network.id)) {
          toggleNode(currentNode.network.id)
        } else {
          moveToParent(currentNode, visibleNodes)
        }
      }
    },
    Enter: (
      e: KeyboardEvent<HTMLInputElement>,
      currentNode: NetworkTreeNode,
      _currentIndex?: number,
      _visibleNodes?: NetworkTreeNode[]
    ) => {
      e.preventDefault()
      if (currentNode && !disabledNodeIds.has(currentNode.network.id)) {
        handleSelect(currentNode)
      }
    },
    Escape: (
      e: KeyboardEvent<HTMLInputElement>,
      _currentNode?: NetworkTreeNode,
      _currentIndex?: number,
      _visibleNodes?: NetworkTreeNode[]
    ) => {
      e.preventDefault()
      setSearchTerm('')
      inputRef.current?.select()
    },
    Home: (
      e: KeyboardEvent<HTMLInputElement>,
      _currentNode?: NetworkTreeNode,
      _currentIndex?: number,
      _visibleNodes?: NetworkTreeNode[]
    ) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        const enabledVisibleNodes = getEnabledVisibleNodes()
        if (enabledVisibleNodes.length > 0) {
          const firstNodeId = enabledVisibleNodes[0].network.id
          setFocusedNodeId(firstNodeId)
          scrollFocusedNodeIntoView(firstNodeId)
        }
      }
    },
    End: (
      e: KeyboardEvent<HTMLInputElement>,
      _currentNode?: NetworkTreeNode,
      _currentIndex?: number,
      _visibleNodes?: NetworkTreeNode[]
    ) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        const enabledVisibleNodes = getEnabledVisibleNodes()
        if (enabledVisibleNodes.length > 0) {
          const lastNodeId =
            enabledVisibleNodes[enabledVisibleNodes.length - 1].network.id
          setFocusedNodeId(lastNodeId)
          scrollFocusedNodeIntoView(lastNodeId)
        }
      }
    }
  }

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isComposing) return

    const visibleNodes = getVisibleNodes()
    const currentIndex = visibleNodes.findIndex(
      node => node.network.id === focusedNodeId
    )
    const currentNode = currentIndex !== -1 ? visibleNodes[currentIndex] : null

    const action = keyboardActions[e.key as keyof typeof keyboardActions]
    if (action && currentNode !== null) {
      action(e, currentNode, currentIndex, visibleNodes)
    }
  }

  return {
    focusedNodeId,
    selectedNodeId,
    handleSelect,
    handleInputKeyDown,
    inputRef,
    setFocusedNodeId
  }
}
